if (!customElements.get('homepage-banner')) {
  customElements.define('homepage-banner', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('[data-banner-track]');
      this.viewport = this.querySelector('[data-banner-viewport]');
      this.slides = [...this.querySelectorAll('[data-banner-slide]')];
      this.pagination = this.querySelector('[data-banner-pagination]');
      this.previousButton = this.querySelector('[data-banner-prev]');
      this.nextButton = this.querySelector('[data-banner-next]');
      this.status = this.querySelector('[data-banner-status]');
      this.index = 0;
      this.pointerStart = null;
      this.autoplay = this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.delay = Math.max(3000, Number(this.dataset.autoplayDelay) || 6000);
      this.loop = this.dataset.loop === 'true';
      this.renderPagination();
      this.listen(this.previousButton, 'click', () => this.go(this.index - 1, true));
      this.listen(this.nextButton, 'click', () => this.go(this.index + 1, true));
      this.listen(this.viewport, 'pointerdown', event => { this.pointerStart = event.clientX; });
      this.listen(this.viewport, 'pointerup', event => this.onSwipe(event.clientX));
      this.listen(this.viewport, 'pointercancel', () => { this.pointerStart = null; });
      this.listen(this, 'mouseenter', () => {
        if (this.dataset.pauseOnHover === 'true') this.stop();
      });
      this.listen(this, 'mouseleave', () => this.start());
      this.listen(this, 'focusin', () => this.stop());
      this.listen(this, 'focusout', event => {
        if (!this.contains(event.relatedTarget)) this.start();
      });
      this.listen(document, 'visibilitychange', () => document.hidden ? this.stop() : this.start());
      this.listen(document, 'shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (selected >= 0) this.go(selected, true);
      });
      this.go(0, false, false);
      this.start();
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
    }
    listen(target, event, handler) {
      target?.addEventListener(event, handler, { signal: this.controller.signal });
    }
    renderPagination() {
      if (!this.pagination) return;
      this.pagination.replaceChildren(...this.slides.map((_, index) => {
        const bullet = document.createElement('button');
        bullet.type = 'button';
        bullet.className = 'homepage-banner__bullet';
        bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
        bullet.addEventListener('click', () => this.go(index, true), { signal: this.controller.signal });
        return bullet;
      }));
    }
    go(nextIndex, manual = false, announce = true) {
      if (this.slides.length < 1) return;
      if (this.loop) nextIndex = (nextIndex + this.slides.length) % this.slides.length;
      else nextIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
      this.index = nextIndex;
      this.track.style.transform = `translate3d(${-100 * this.index}%, 0, 0)`;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach(element => element.tabIndex = active ? 0 : -1);
        const video = slide.querySelector('video');
        if (video) active ? video.play().catch(() => {}) : video.pause();
      });
      [...(this.pagination?.children || [])].forEach((bullet, index) => {
        const active = index === this.index;
        bullet.classList.toggle('is-active', active);
        bullet.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (this.previousButton) this.previousButton.disabled = !this.loop && this.index === 0;
      if (this.nextButton) this.nextButton.disabled = !this.loop && this.index === this.slides.length - 1;
      if (announce && this.status) this.status.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
      if (manual) { this.stop(); this.start(); }
    }
    onSwipe(pointerEnd) {
      if (this.pointerStart === null) return;
      const distance = pointerEnd - this.pointerStart;
      this.pointerStart = null;
      if (Math.abs(distance) < 50) return;
      this.go(this.index + (distance < 0 ? 1 : -1), true);
    }
    start() {
      this.stop();
      if (!this.autoplay || this.slides.length < 2 || document.hidden) return;
      this.timer = window.setInterval(() => {
        if (!this.loop && this.index === this.slides.length - 1) {
          this.stop();
          return;
        }
        this.go(this.index + 1);
      }, this.delay);
    }
    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  });
}
